import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CardBorder, CardLevel } from '@freelancer/ui/card';
import type { RadioOptionItem } from '@freelancer/ui/radio';
import { isDefined } from '@freelancer/utils';
import {
  BackgroundColor,
  HoverColor,
  ImageAlignment,
  RadioCardPadding,
  RadioCardSelectedColor,
} from './radio-card.types';

@Component({
  standalone: false,
  selector: 'fl-radio-card',
  template: `
    <fl-card
      class="RadioCard"
      [attr.data-background-color]="backgroundColor"
      [attr.data-border]="border"
      [attr.data-disabled]="disabled"
      [attr.data-disable-min-height]="disableMinHeight"
      [attr.data-disable-shadow]="disableShadow"
      [attr.data-hover-color]="hoverColor"
      [attr.data-is-selected]="isSelected"
      [attr.data-padding]="padding"
      [attr.data-selected-color]="selectedColor"
      [attr.tabindex]="disabled ? -1 : 1"
      [attr.aria-disabled]="disabled"
      [border]="border"
      [clickable]="!disabled"
      [disableFullHeight]="true"
      [edgeToEdge]="true"
      [level]="level"
      (click)="handleCardClicked(value)"
      (keyup.enter)="handleCardEnter(value)"
    >
      <div class="Container">
        @if (showRadio) {
          <div class="Left">
            <div class="Mask"></div>
            <fl-radio
              class="Radio"
              [control]="control"
              [forListItem]="true"
              [options]="[toRadioOptionItem(value)]"
            ></fl-radio>
          </div>
        }

        @if (pictureSrc) {
          <fl-grid>
            <fl-col
              class="RadioCardPicture"
              [col]="3"
              [attr.data-align-items]="imageAlignment"
            >
              <fl-picture
                [alt]="pictureAlt"
                [fullWidth]="true"
                [src]="pictureSrc"
              ></fl-picture>
            </fl-col>
            <fl-col [col]="9">
              <ng-container *ngTemplateOutlet="content"></ng-container>
            </fl-col>
          </fl-grid>
        } @else {
          <ng-container *ngTemplateOutlet="content"></ng-container>
        }
      </div>
      <ng-template #content>
        <ng-content></ng-content>
      </ng-template>
    </fl-card>
  `,
  styleUrls: ['./radio-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RadioCardComponent<T> {
  @Input() control: FormControl<T>;
  @Input() pictureSrc?: string;
  @Input() pictureAlt: string;
  @Input() isSelected: boolean;
  // This forces TypeScript to infer T from `FormControl<T>`
  @Input() value: T extends T ? T : T;
  @Input() backgroundColor?: BackgroundColor;
  @Input() hoverColor = HoverColor.MID;
  @Input() selectedColor?: RadioCardSelectedColor =
    RadioCardSelectedColor.SECONDARY;

  @Input() showRadio = false;
  @Input() level: CardLevel = 'primary';
  @Input() border: CardBorder = 'none';
  @Input() disabled = false;
  @Input() disableMinHeight = false;
  @Input() disableShadow = false;
  @Input() padding = RadioCardPadding.MID;
  @Input() imageAlignment: ImageAlignment = ImageAlignment.CENTER;

  handleCardClicked(value?: T): void {
    if (this.disabled) {
      return;
    }
    if (isDefined(value)) {
      this.control.setValue(value);
    }
  }

  handleCardEnter(value?: T): void {
    if (this.disabled) {
      return;
    }
    if (isDefined(value)) {
      this.control.setValue(value);
    }
  }

  toRadioOptionItem(value: T): RadioOptionItem<T> {
    return {
      displayText: String(value),
      value,
    };
  }
}
